import { CANADA_PROVINCE_ABBREVIATIONS } from 'constants/CanadaProvinceTimezones';
import { US_STATE_ABBREVIATIONS } from 'constants/USStateTimezones';

const MGB_STOP_STATE_ALLOWLIST = new Set([
  ...US_STATE_ABBREVIATIONS,
  ...CANADA_PROVINCE_ABBREVIATIONS,
]);

const MGB_ALLOWED_COUNTRY_CODES = new Set(['USA', 'US', 'CA', 'CAN', 'CANADA']);

/** Trim + uppercase for stop geography codes (state/province or country). */
export function normalizeMGBStopState(code: string): string {
  return code.trim().toUpperCase();
}

/** True when state is blank or a known US state / Canadian province code. */
export function isValidMGBStopState(state: string): boolean {
  const normalized = normalizeMGBStopState(state);
  if (!normalized) {
    return true;
  }
  return MGB_STOP_STATE_ALLOWLIST.has(normalized);
}

/** Blank country is allowed; otherwise only USA / Canada codes accepted. */
export function isAllowedMGBStopCountry(country: string): boolean {
  const normalized = normalizeMGBStopState(country);
  if (!normalized) {
    return true;
  }
  return MGB_ALLOWED_COUNTRY_CODES.has(normalized);
}

/** Infer MercuryGate country code from a validated stop state/province. */
export function resolveMGBStopCountryCode(state: string): string {
  const normalized = normalizeMGBStopState(state);
  if (CANADA_PROVINCE_ABBREVIATIONS.includes(normalized)) {
    return 'CA';
  }
  return 'USA';
}

/**
 * Country UI was removed; forms still default/legacy-load `country: 'USA'`.
 * When state is present, always derive country from it so CA provinces are not
 * stuck with a stale USA default.
 */
export function resolveMGBStopCountryForSubmit(
  country: string | undefined,
  state: string | undefined
): string {
  const stateCode = normalizeMGBStopState(state ?? '');
  if (stateCode) {
    return resolveMGBStopCountryCode(stateCode);
  }

  const existing = normalizeMGBStopState(country ?? '');
  if (!existing) {
    return 'USA';
  }

  if (existing === 'CA' || existing === 'CAN' || existing === 'CANADA') {
    return 'CA';
  }
  // USA / US / legacy free-text all default to USA (matches BE).
  return 'USA';
}

/** Overwrite stop.country for create/update payloads using submit resolution rules.
 * Also uppercases state so mixed-case form values (e.g. `fl`) match BE allowlists.
 */
export function withResolvedMGBStopCountry<
  T extends { country?: string; state?: string },
>(stop: T): T {
  const state = normalizeMGBStopState(stop.state ?? '');
  return {
    ...stop,
    ...(state ? { state } : {}),
    country: resolveMGBStopCountryForSubmit(stop.country, state || stop.state),
  };
}
